import type { FC } from 'react';
import { use, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { SiteContext } from 'src/context/site-context';
import { replaceTextWithFixedValues } from 'src/helpers/string-helper';

import { Language } from '../enums/language';

import { staticTextLookup as enGbStaticTextLookup } from './static-text/en-gb';
import type { StaticTextLookup, StaticTextLookupKey } from './static-text-lookup';

export const fallbackLanguage = Language.EN_GB as const;
const fallbackStaticTextLookup: StaticTextLookup = enGbStaticTextLookup;

const lookupCache = new Map<Language, Partial<StaticTextLookup>>([
  [fallbackLanguage, fallbackStaticTextLookup],
]);

const lookupCacheListeners = new Set<() => void>();

const subscribeToLookupCache = (onLookupCacheChange: () => void) => {
  lookupCacheListeners.add(onLookupCacheChange);
  return () => {
    lookupCacheListeners.delete(onLookupCacheChange);
  };
};

const getLookupIfLoadedOrFallback = (language: Language): Partial<StaticTextLookup> => {
  const cached = lookupCache.get(language);
  if (cached) return cached;

  // During SSG, ensure that all text is rendered in the correct language.
  // Due to DefinePlugin, this becomes statically-dead code during the client build, so Webpack (correctly) doesn't
  // include all static text in the main app bundle.
  if (process.env.BUILD_STAGE === 'build-html' || process.env.BUILD_STAGE === 'develop-html') {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const mod = require(`./static-text/${language}`) as {
      staticTextLookup: Partial<StaticTextLookup>;
    };
    lookupCache.set(language, mod.staticTextLookup);
    return mod.staticTextLookup;
  }

  return fallbackStaticTextLookup;
};

// TODO Redesign: replace with a better promise cache API
const pendingLanguages = new Map<Language, Promise<Partial<StaticTextLookup>>>();

export const loadLookupAsync = (language: Language): Promise<Partial<StaticTextLookup>> => {
  const cached = lookupCache.get(language);
  if (cached) return Promise.resolve(cached);
  let pending = pendingLanguages.get(language);
  if (!pending) {
    // eslint-disable-next-line no-console
    console.info(`Loading locale ${language}`);
    pending = import(`./static-text/${language}`).then(
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Typings across non-fallback languages is consistent
      (mod: typeof import('./static-text/en-de')) => {
        lookupCache.set(language, mod.staticTextLookup);
        pendingLanguages.delete(language);
        lookupCacheListeners.forEach((notify) => notify());
        return mod.staticTextLookup;
      }
    );
    pendingLanguages.set(language, pending);
  }
  return pending;
};

export type StaticTextTranslator = (
  key: StaticTextLookupKey,
  params?: Record<string, string>
) => string;

const buildTranslator =
  (lookup: Partial<StaticTextLookup>): StaticTextTranslator =>
  (key, params) => {
    const text = lookup[key] ?? fallbackStaticTextLookup[key];
    return params ? replaceTextWithFixedValues(text, params) : text;
  };

export const loadTranslator = (language: Language): Promise<StaticTextTranslator> =>
  loadLookupAsync(language).then(buildTranslator);

const useLookup = (language: Language): Partial<StaticTextLookup> => {
  const getSnapshot = useCallback(() => getLookupIfLoadedOrFallback(language), [language]);

  useEffect(() => {
    void loadLookupAsync(language);
  }, [language]);

  return useSyncExternalStore(subscribeToLookupCache, getSnapshot, getSnapshot);
};

const useTranslator = (language: Language): StaticTextTranslator => {
  const lookup = useLookup(language);
  return useMemo(() => buildTranslator(lookup), [lookup]);
};

export const useStaticTextTranslator = (): StaticTextTranslator => {
  const { language } = use(SiteContext);
  return useTranslator(language);
};

type TranslatedStaticTextProps = {
  lookupKey: StaticTextLookupKey;
  params?: Record<string, string>;
  languageOverride?: Language;
};

export const TranslatedStaticText: FC<TranslatedStaticTextProps> = ({
  lookupKey,
  params,
  languageOverride,
}) => {
  const { language: pageLanguage } = use(SiteContext);
  const translate = useTranslator(languageOverride ?? pageLanguage);
  return translate(lookupKey, params);
};
